// The call state machine. Pure and framework-free so it can be unit tested
// with `node --test` and reused as-is by the web client and, later, the app.
//
// States a call screen actually renders. "reconnecting" is deliberately not
// its own top-level state: per the brief, a dropped connection shows
// "Reconnecting" on the affected tile only, and the LOCAL call stays
// "connected" the whole time. Only a full local network loss moves the
// whole call to "reconnecting".

export type CallState =
  | { readonly phase: "idle" }
  | { readonly phase: "previewing" }
  | { readonly phase: "connecting" }
  | { readonly phase: "waitingForApproval"; readonly knockId: string; readonly sfuReady?: boolean }
  | { readonly phase: "connected" }
  | { readonly phase: "reconnecting"; readonly attempt: number }
  | { readonly phase: "ended"; readonly reason: EndReason }
  | { readonly phase: "error"; readonly message: string };

/** "movedAway": the same account took this seat to another device (a join
 * with `move=1`); this screen can offer to move it back. */
export type EndReason = "left" | "roomClosed" | "knockDenied" | "kicked" | "movedAway";

export type CallEvent =
  | { readonly type: "PREVIEW_READY" }
  | { readonly type: "JOIN_REQUESTED" }
  | { readonly type: "KNOCK_SENT"; readonly knockId: string }
  | { readonly type: "KNOCK_APPROVED" }
  | { readonly type: "KNOCK_DENIED" }
  | { readonly type: "SFU_CONNECTED" }
  | { readonly type: "NETWORK_LOST" }
  | { readonly type: "NETWORK_RESTORED" }
  | { readonly type: "RECONNECT_FAILED" }
  | { readonly type: "ROOM_CLOSED" }
  | { readonly type: "LEAVE" }
  | { readonly type: "MOVED_AWAY" }
  | { readonly type: "ERROR"; readonly message: string };

export const INITIAL_CALL_STATE: CallState = { phase: "idle" };

// About 100 seconds of tries on the web (backoff 1, 2, 4, then 8 seconds),
// longer on iOS, where each try may wait 15 seconds. The room keeps a seat
// for 10 minutes after its grace window runs out, so a phone that comes back
// in that time walks straight in; giving up at 31 seconds (the old 6) ended
// calls the room would still have taken back.
const MAX_RECONNECT_ATTEMPTS = 15;

export function transition(state: CallState, event: CallEvent): CallState {
  switch (event.type) {
    case "ERROR":
      // An error always wins: it is the one event that can happen in any
      // state and needs to be shown regardless of what the call was doing.
      return { phase: "error", message: event.message };

    case "LEAVE":
      return { phase: "ended", reason: "left" };

    case "ROOM_CLOSED":
      return { phase: "ended", reason: "roomClosed" };

    case "MOVED_AWAY":
      return { phase: "ended", reason: "movedAway" };
  }

  switch (state.phase) {
    case "idle":
      if (event.type === "PREVIEW_READY") return { phase: "previewing" };
      return state;

    case "previewing":
      if (event.type === "JOIN_REQUESTED") return { phase: "connecting" };
      return state;

    case "connecting":
      if (event.type === "KNOCK_SENT") {
        return { phase: "waitingForApproval", knockId: event.knockId };
      }
      if (event.type === "SFU_CONNECTED") return { phase: "connected" };
      // A guest let in whose media never came up (a first publish that
      // failed) was left here for good, since nothing else leaves this
      // phase. Losing the connection here reconnects, as it does in a call.
      if (event.type === "NETWORK_LOST") return { phase: "reconnecting", attempt: 1 };
      return state;

    case "waitingForApproval":
      // The media connection finishes while the guest waits in the lobby,
      // and it does not announce itself twice. Dropping SFU_CONNECTED here
      // left an admitted guest on "Connecting" for good, unseen by the host
      // (2026-10-04, a guest let in from the iOS app). So it is remembered,
      // and letting them in puts them straight into the call.
      if (event.type === "SFU_CONNECTED") return { ...state, sfuReady: true };
      if (event.type === "KNOCK_APPROVED") return state.sfuReady ? { phase: "connected" } : { phase: "connecting" };
      if (event.type === "KNOCK_DENIED") return { phase: "ended", reason: "knockDenied" };
      return state;

    case "connected":
      if (event.type === "NETWORK_LOST") return { phase: "reconnecting", attempt: 1 };
      // The room no longer knew this seat and sent it to the door. Ignoring
      // that left a guest on "Reconnecting" until the tries ran out and the
      // call ended under her; at the door the host can let her back in.
      if (event.type === "KNOCK_SENT") return { phase: "waitingForApproval", knockId: event.knockId, sfuReady: true };
      return state;

    case "reconnecting":
      if (event.type === "NETWORK_RESTORED") return { phase: "connected" };
      if (event.type === "KNOCK_SENT") return { phase: "waitingForApproval", knockId: event.knockId, sfuReady: true };
      if (event.type === "RECONNECT_FAILED") {
        const attempt = state.attempt + 1;
        if (attempt > MAX_RECONNECT_ATTEMPTS) {
          return { phase: "ended", reason: "roomClosed" };
        }
        return { phase: "reconnecting", attempt };
      }
      return state;

    case "ended":
    case "error":
      // Terminal. A fresh call is a fresh state machine, not a transition
      // out of this one.
      return state;
  }
}

export function isCallActive(state: CallState): boolean {
  return (
    state.phase === "connecting" ||
    state.phase === "waitingForApproval" ||
    state.phase === "connected" ||
    state.phase === "reconnecting"
  );
}
